import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../../contexts/PlatformAuthContext';
import { ShieldCheckIcon, BuildingOffice2Icon, ClockIcon, ArrowRightOnRectangleIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

/*
 * Copyright (c) 2026 Notiosoft SA.
 * All rights reserved.
 *
 * Deliberately dark / visually distinct from the tenant admin Layout so an
 * operator can never mistake "I'm in the platform console" for "I'm inside
 * a municipality's admin portal" at a glance.
 */

const NAV = [
  { name: 'Municipalities', href: '/platform-ops', icon: BuildingOffice2Icon },
  { name: 'Billing', href: '/platform-ops/billing', icon: CurrencyDollarIcon },
  { name: 'Audit Log', href: '/platform-ops/audit-log', icon: ClockIcon },
];

const PlatformLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { operator, logout } = usePlatformAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/platform-ops/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-emerald-400" />
              <span className="text-sm font-semibold tracking-wide text-white">PLATFORM CONSOLE</span>
            </div>
            <nav className="flex items-center gap-1">
              {NAV.map((item) => {
                const active = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      active ? 'bg-emerald-500/10 text-emerald-300' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{operator?.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
    </div>
  );
};

export default PlatformLayout;
